import { productApi } from "#/api/productApi";
import ActionButton from "#/components/buttons/ActionButton";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import type { CreateProductPayload } from "#/types/products";
import { ItemStatus } from "#/types/items";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { getRandomColor, getItemStatusColor } from "#/utils/ColorUtils";
import { Accordion, Button, Select, Textarea, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconExternalLink, IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useAllowedStatuses } from "#/hooks/useAllowedStatuses";

const ProductDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { canWrite, canDelete } = usePermission();
  const allowedStatuses = useAllowedStatuses();
  const [searchParams] = useSearchParams();
  const duplicateFrom = searchParams.get("duplicateFrom");

  // Helper function to check if a value is a valid ItemStatus enum
  const isValidItemStatus = (value: string | undefined): boolean => {
    if (!value) return false;
    return Object.values(ItemStatus).includes(value as ItemStatus);
  };

  const form = useForm<CreateProductPayload>({
    initialValues: {
      name: "",
      description: "",
      sku: "",
      variant: "",
      cost: undefined,
      retail_price: undefined,
      tare_weight: undefined,
      filling_capacity: undefined,
      scan_statuses: [{ step: 1, label: "" }],
      customize_columns: {},
    },
    validate: (values) => {
      console.log("🚀 ~ ProductDetailPage ~ values:", values);
      const errors: Record<string, string> = {};

      if (!values.name) errors.name = t("products.form.namePlaceholder");
      if (id === "new" && !values.sku) errors.sku = t("products.form.sku");
      // if (!values.description) errors.description = "Description is required";
      // if (!values.variant) errors.variant = "Variant is required";
      // if (values.cost == null) errors.cost = "Cost is required";
      // if (values.retail_price == null)
      //   errors.retail_price = "Retail price is required";
      // if (values.tare_weight == null)
      //   errors.tare_weight = "Tare weight is required";
      // if (values.filling_capacity == null)
      //   errors.filling_capacity = "Filling capacity is required";

      // if (!values.scan_statuses || values.scan_statuses.length === 0) {
      //   errors["scan_statuses"] = "At least one scan step is required";
      // } else {
      //   values.scan_statuses.forEach((item, index) => {
      //     if (!item.label || item.label.trim() === "") {
      //       errors[`scan_statuses.${index}.label`] = "Step cannot be empty";
      //     }
      //   });
      // }

      return errors;
    },
    transformValues: (values) => ({
      ...values,
      cost: values.cost ? Number(values.cost) : undefined,
      retail_price: values.retail_price
        ? Number(values.retail_price)
        : undefined,
      tare_weight: values.tare_weight ? Number(values.tare_weight) : undefined,
      filling_capacity: values.filling_capacity
        ? Number(values.filling_capacity)
        : undefined,
      scan_statuses: values.scan_statuses?.map((item, index) => {
        return {
          step: index + 1,
          label: item.label,
        };
      }),
    }),
  });

  const handleSubmit = (values: typeof form.values) => {
    // Filter out empty/null/undefined fields
    const payload: Record<string, unknown> = {};

    (Object.entries(values) as [keyof CreateProductPayload, CreateProductPayload[keyof CreateProductPayload]][]).forEach(([key, value]) => {
      // Skip empty strings, null, undefined
      if (value === "" || value === null || value === undefined) {
        return;
      }

      // Skip empty arrays
      if (Array.isArray(value) && value.length === 0) {
        return;
      }

      // Skip empty objects
      if (typeof value === "object" && !Array.isArray(value) && Object.keys(value as Record<string, unknown>).length === 0) {
        return;
      }

      // For scan_statuses, filter out empty labels
      if (key === "scan_statuses" && Array.isArray(value)) {
        const filtered = (value as { step: number; label: string }[]).filter(
          (item) =>
            typeof item === "object" &&
            item !== null &&
            "label" in item &&
            typeof item.label === "string" &&
            item.label.trim() !== ""
        );
        if (filtered.length > 0) {
          payload[key] = filtered;
        }
        return;
      }

      payload[key] = value;
    });

    if (id && product && product.sku) {
      delete payload.sku;
    }

    productMutation.mutate(payload);
  };

  const productMutation = useMutation({
    mutationFn: async (payload: CreateProductPayload) => {
      if (id !== "new") {
        return await productApi.update(Number(id), payload);
      } else {
        return await productApi.create(payload);
      }
    },
    onSuccess: (data) => {
      if (id === "new") {
        void navigate(`/inventory/categories/${data.data.id}`);
      }
      showSuccessNotification({
        message: id === "new" ? t("products.createCategory") + " - " + t("common.success") : t("products.categoryDetails") + " - " + t("common.success"),
      });
    },
    onError: (error) => {
      console.log("🚀 ~ ProductDetail ~ error:", error);
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  const { data: product } = useQuery({
    queryKey: ["productDetails", id],
    enabled: !!id && id !== "new",
    queryFn: async () => {
      if (!id || id === "new") throw new Error("Invalid categoryId");

      const response = await productApi.getById(Number(id));
      if (!response.data) throw new Error("Failed to fetch category");
      const product = response.data;
      form.setValues({
        name: product.name ?? "",
        description: product.description ?? "",
        sku: product.sku ?? "",
        variant: product.variant ?? "",
        cost: product.cost ?? undefined,
        retail_price: product.retail_price ?? undefined,
        tare_weight: product.tare_weight ?? 0,
        filling_capacity: product.filling_capacity ?? 0,
        customize_columns: { ...(product.customize_columns ?? {}) },
        scan_statuses: product.scan_statuses ?? [],
      });
      form.resetDirty();
      return product;
    },
  });

  // Fetch product to duplicate
  useQuery({
    queryKey: ["duplicateProduct", duplicateFrom],
    enabled: !!duplicateFrom && id === "new",
    queryFn: async () => {
      if (!duplicateFrom) throw new Error("Invalid category to duplicate");

      const response = await productApi.getById(Number(duplicateFrom));
      if (!response.data) throw new Error("Failed to fetch category to duplicate");
      const product = response.data;

      // Pre-fill form with duplicated product data (excluding id and SKU)
      form.setValues({
        name: product.name ? `${product.name} (Copy)` : "",
        description: product.description ?? "",
        sku: "", // Don't copy SKU - it must be unique
        variant: product.variant ?? "",
        cost: product.cost ?? undefined,
        retail_price: product.retail_price ?? undefined,
        tare_weight: product.tare_weight ?? 0,
        filling_capacity: product.filling_capacity ?? 0,
        customize_columns: { ...(product.customize_columns ?? {}) },
        scan_statuses: product.scan_statuses ?? [],
      });
      form.resetDirty();
      return product;
    },
  });

  return (
    <DetailPageLayout
      title={id != "new" ? t("products.categoryDetails") : t("products.createCategory")}
      navRightSection={
        <div className={`w-full flex  justify-end gap-2 items-center`}>
          <Button
            color="black"
            variant="outline"
            type="reset"
            disabled={!form.isDirty()}
            onClick={() => {
              form.reset();
            }}
            size="xs"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={productMutation.isPending}
              disabled={!form.isDirty() || !canWrite()}
              onClick={() => {
                form.onSubmit(handleSubmit)();
              }}
              size="xs"
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      }
    >
      <div>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <div className="w-full grid grid-cols-[70%_1fr] gap-4">
            <div className="w-full grid grid-cols-1 gap-4">
              <Accordion
                variant={"separated"}
                defaultValue={["basic", "steps", "customize_columns"]}
                multiple
              >
                <Accordion.Item value={"basic"}>
              <Accordion.Control>{t("common.basicDetails")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full flex flex-col gap-4 ">
                  <TextInput
                    label={t("products.form.name")}
                    withAsterisk
                    disabled={!canWrite()}
                    placeholder={t("products.form.namePlaceholder")}
                    {...form.getInputProps("name")}
                  />

                  <div className={`w-full grid gap-4 ${(id === "new" || !product?.sku) ? "grid-cols-4" : "grid-cols-3"}`}>
                    {(id === "new" || !product?.sku) && (
                      <TextInput
                        label={t("products.form.sku")}
                        withAsterisk={id === "new"}
                        disabled={!canWrite()}
                        placeholder={t("products.form.sku")}
                        {...form.getInputProps("sku")}
                      />
                    )}

                    <TextInput
                      label={t("products.form.type")}
                      disabled={!canWrite()}
                      placeholder={t("products.form.type")}
                      {...form.getInputProps("variant")}
                    />

                    <TextInput
                      label={t("products.columns.cost")}
                      type="number"
                      disabled={!canWrite()}
                      placeholder={t("products.columns.cost")}
                      {...form.getInputProps("cost")}
                    />
                    <TextInput
                      label={t("products.columns.retailPrice")}
                      type="number"
                      disabled={!canWrite()}
                      placeholder={t("products.columns.retailPrice")}
                      {...form.getInputProps("retail_price")}
                    />
                  </div>

                  <div className="w-full grid grid-cols-2 gap-4">
                    <TextInput
                      label={t("products.columns.fillingCapacity")}
                      description={t("products.columns.fillingCapacity")}
                      type="number"
                      disabled={!canWrite()}
                      placeholder={t("products.columns.fillingCapacity")}
                      {...form.getInputProps("filling_capacity")}
                    />

                    <TextInput
                      label={t("products.columns.tareWeight")}
                      description={t("products.columns.tareWeight")}
                      type="number"
                      disabled={!canWrite()}
                      placeholder={t("products.columns.tareWeight")}
                      {...form.getInputProps("tare_weight")}
                    />
                  </div>

                  <Textarea
                    label={t("products.form.description")}
                    minRows={20}
                    disabled={!canWrite()}
                    classNames={{
                      input: "h-38",
                    }}
                    placeholder={t("products.form.descriptionPlaceholder")}
                    {...form.getInputProps("description")}
                  />
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value={"steps"}>
              <Accordion.Control>
                <div className="flex items-center gap-2">
                  <span>
                    {t("products.scanSteps")}{" "}
                    {(form.values.scan_statuses?.length ?? 0) > 0
                      ? `( ${form.values.scan_statuses!.length} )`
                      : ""}
                  </span>
                  <PermissionTooltip disabled={!canWrite()}>
                    <ActionButton
                      color="black"
                      size={"xs"}
                      icon={IconPlus}
                      disabled={
                        !canWrite() ||
                        (form.values.scan_statuses &&
                        form.values.scan_statuses.length >= 10)
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        form.setValues({
                          ...form.values,
                          scan_statuses: [
                            ...(form?.values?.scan_statuses ?? []),
                            {
                              step: form.values?.scan_statuses
                                ? form.values?.scan_statuses.length + 1
                                : 1,
                              label: "",
                            },
                          ],
                        });
                      }}
                    />
                  </PermissionTooltip>
                </div>
              </Accordion.Control>
              <Accordion.Panel>
                <div className="w-full flex flex-col gap-4">
                  <div className="w-full flex flex-col gap-1">
                    {form.values?.scan_statuses?.map((scanStatus, index) => {
                      const currentLabel = scanStatus.label;
                      const isValidStatus = isValidItemStatus(currentLabel);

                      return (
                        <div
                          key={`step_${index}`}
                          className="w-full grid grid-cols-[1fr_auto] gap-4 items-center"
                        >
                          <Select
                            label={`${t("products.scanSteps")} # ${index + 1}`}
                            placeholder={t("common.selectStatus")}
                            searchable
                            disabled={!canWrite()}
                            description={
                              !isValidStatus && currentLabel
                                ? `Old Value: ${currentLabel}`
                                : undefined
                            }
                            data={allowedStatuses.map((status) => ({
                              value: status,
                              label: sanitizeItemStatusLabel(status),
                            }))}
                            {...form.getInputProps(
                              `scan_statuses.${index}.label`
                            )}
                          />

                          <div className="mt-6">
                            <PermissionTooltip disabled={!canDelete()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="white"
                                disabled={index === 0 || !canDelete()}
                                color="red.5"
                                onClick={() => {
                                  const newSteps = [
                                    ...(form.values.scan_statuses ?? []),
                                  ];
                                  newSteps.splice(index, 1);
                                  form.setFieldValue("scan_statuses", newSteps);
                                }}
                              />
                            </PermissionTooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="customize_columns">
              <Accordion.Control>
                <div className="flex items-center gap-2">
                  <span>{t("common.customFields")}</span>
                  <PermissionTooltip disabled={!canWrite()}>
                    <ActionButton
                      color="black"
                      size="xs"
                      icon={IconPlus}
                      disabled={!canWrite()}
                      onClick={(e) => {
                        e.stopPropagation();
                        const existing = { ...form.values.customize_columns };
                        let newKey = `field_${Object.keys(existing).length + 1}`;
                        while (newKey in existing) {
                          newKey += "_";
                        }
                        form.setFieldValue("customize_columns", {
                          ...existing,
                          [newKey]: "",
                        });
                      }}
                    />
                  </PermissionTooltip>
                </div>
              </Accordion.Control>

              <Accordion.Panel>
                {Object.entries(form.values.customize_columns ?? {}).length <=
                0 ? (
                  <div>{t("common.addCustomFieldHint")}</div>
                ) : (
                  <div className="w-full flex flex-col gap-2">
                    {Object.entries(form.values.customize_columns ?? {}).map(
                      ([key, value], index) => {
                        const fieldId = `${key}_${index}`;

                        return (
                          <div
                            key={fieldId}
                            className="w-full grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center"
                          >
                            {/* Editable Key input */}
                            <span className="bg-black/5 font-bold text-black/70 rounded-full p-2 w-8 h-8 flex items-center justify-center">
                              <span>{index + 1}</span>
                            </span>
                            <TextInput
                              defaultValue={key}
                              disabled={!canWrite()}
                              onBlur={(event) => {
                                const newKey = event.currentTarget.value.trim();
                                if (!newKey || newKey === key) return;

                                const updated = {
                                  ...form.values.customize_columns,
                                };
                                const val = updated[key];
                                delete updated[key];

                                if (newKey in updated) return; // avoid duplicates

                                updated[newKey] = val;
                                form.setFieldValue(
                                  "customize_columns",
                                  updated
                                );
                              }}
                            />

                            {/* Editable Value input */}
                            <TextInput
                              value={value}
                              disabled={!canWrite()}
                              onChange={(event) => {
                                form.setFieldValue(
                                  `customize_columns.${key}`,
                                  event.currentTarget.value
                                );
                              }}
                            />

                            {/* Delete Button */}
                            <PermissionTooltip disabled={!canDelete()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="white"
                                color="red.5"
                                disabled={!canDelete()}
                                onClick={() => {
                                  const updated = {
                                    ...form.values.customize_columns,
                                  };
                                  delete updated[key];
                                  form.setFieldValue(
                                    "customize_columns",
                                    updated
                                  );
                                }}
                              />
                            </PermissionTooltip>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>
              </Accordion>
            </div>

            <div className="w-full grid grid-cols-1 gap-4">
              {id !== "new" && product && (
                <Accordion
                  variant={"separated"}
                  defaultValue={product.sku ? ["sku", "analytics"] : ["analytics"]}
                  multiple
                >
                  {product.sku && (
                    <Accordion.Item value={"sku"} disabled>
                      <Accordion.Control>SKU</Accordion.Control>
                      <Accordion.Panel>
                        <div className="text-base font-medium">{product.sku}</div>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}

                  <Accordion.Item value={"analytics"}>
                    <Accordion.Control>{t("common.analytics")}</Accordion.Control>
                    <Accordion.Panel>
                      <div className="w-full flex flex-col gap-4">
                        {/* Total Items Card */}
                        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                          <div className="text-sm font-semibold text-emerald-800">{t("products.analytics.totalItems")}</div>
                          <div className="text-3xl font-extrabold mt-2 text-emerald-600">
                            {product.status_breakdown
                              ? Object.values(product.status_breakdown).reduce((sum, count) => sum + count, 0)
                              : 0}
                          </div>
                        </div>

                        {/* Sites Card */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="text-sm font-semibold">{t("products.analytics.sites")}</div>
                          <div className="text-xs text-gray-500 mb-4">{t("products.analytics.distributionBySites")}</div>
                          {product.sites_data && product.sites_data.length > 0 ? (
                            <div className="flex flex-wrap gap-3">
                              {product.sites_data.map((site) => {
                                const color = getRandomColor(site.name);
                                return (
                                  <div
                                    key={site.name}
                                    className="border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow min-w-[120px]"
                                    style={{
                                      borderColor: `var(--mantine-color-${color}-3)`,
                                      backgroundColor: `var(--mantine-color-${color}-0)`,
                                    }}
                                    onClick={() => window.open(`/sites/${site.id}`, '_blank')}
                                  >
                                    <div className="flex justify-between items-start mb-3">
                                      <div
                                        className="text-2xl font-extrabold"
                                        style={{ color: `var(--mantine-color-${color}-7)` }}
                                      >
                                        {site.count}
                                      </div>
                                      <IconExternalLink size={14} className="opacity-60" />
                                    </div>
                                    <div className="text-sm font-medium text-gray-700">{site.name}</div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-400 text-center py-4">{t("products.analytics.noData")}</div>
                          )}
                        </div>

                        {/* Status Card */}
                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="text-sm font-semibold">{t("products.analytics.status")}</div>
                          <div className="text-xs text-gray-500 mb-4">{t("products.analytics.breakdownByStatus")}</div>
                          {product.status_breakdown && Object.keys(product.status_breakdown).length > 0 ? (
                            <div className="flex flex-wrap gap-3">
                              {Object.entries(product.status_breakdown).map(([status, count]) => {
                                const color = getItemStatusColor(status);
                                return (
                                  <div
                                    key={status}
                                    className="border rounded-lg p-4 cursor-pointer hover:shadow-md transition-shadow min-w-[120px]"
                                    style={{
                                      borderColor: `var(--mantine-color-${color}-3)`,
                                      backgroundColor: `var(--mantine-color-${color}-0)`
                                    }}
                                    onClick={() => window.open(`/inventory/items?status=${status}&categoryId=${id}`, '_blank')}
                                  >
                                    <div className="flex justify-between items-start mb-3">
                                      <div
                                        className="text-2xl font-extrabold"
                                        style={{ color: `var(--mantine-color-${color}-7)` }}
                                      >
                                        {count}
                                      </div>
                                      <IconExternalLink size={14} className="opacity-60" />
                                    </div>
                                    <div className="text-sm font-medium text-gray-700">{sanitizeItemStatusLabel(status)}</div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-sm text-gray-400 text-center py-4">{t("products.analytics.noData")}</div>
                          )}
                        </div>
                      </div>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion>
              )}
            </div>
          </div>
        </form>
      </div>
    </DetailPageLayout>
  );
};

export default ProductDetailPage;
